import React, { useEffect, useState } from "react"
import Icon from "../../../components/AppIcon"
import { Card, CardContent } from "../../../components/ui/card"
import {
  computeRevenueKPIs,
  computeCustomerKPIs,
  computeCustomerValueMetrics,
  computeRevenueByProduct,
  getMonthlyRevenue,
} from "../../../utils/dashboardAnalyticsHelpers"

const formatCurrency = (n) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`

const KpiCard = ({ label, value, change, icon }) => (
  <Card className="p-4 dark:bg-transparent">
    <div className="flex items-center justify-between">
      <p className="text-xs text-muted-foreground">{label}</p>
      <Icon name={icon} size={14} className="text-muted-foreground" />
    </div>
    <p className="text-2xl font-bold text-foreground mt-0.5">{value}</p>
    {change !== null && change !== undefined && (
      <p className={`text-xs mt-1 ${change >= 0 ? "text-success" : "text-error"}`}>
        {change >= 0 ? "+" : ""}
        {change}% vs previous period
      </p>
    )}
  </Card>
)

const MiniBarChart = ({ data, valueKey, labelKey }) => {
  const max = Math.max(1, ...(data || []).map((d) => d[valueKey]))
  return (
    <div className="flex items-end gap-1 h-24 mt-3">
      {data?.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            title={`${d[labelKey]}: ${formatCurrency(d[valueKey])}`}
            className="w-full bg-primary/30 hover:bg-primary/60 rounded-sm transition-colors"
            style={{ height: `${Math.max(4, (d[valueKey] / max) * 100)}%` }}
          />
          <span className="text-[9px] text-muted-foreground">{d[labelKey]}</span>
        </div>
      ))}
    </div>
  )
}

const IntelligenceCommercePanel = ({ organization }) => {
  const orgId = organization?.orgId
  const [revenue, setRevenue] = useState(null)
  const [customers, setCustomers] = useState(null)
  const [customerValue, setCustomerValue] = useState(null)
  const [byProduct, setByProduct] = useState([])
  const [monthly, setMonthly] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!orgId) return
    const load = async () => {
      setLoading(true)
      const [rev, cust, custVal, prodRev, monthlyRev] = await Promise.all([
        computeRevenueKPIs(orgId, "30d"),
        computeCustomerKPIs(orgId, "30d"),
        computeCustomerValueMetrics(orgId, "30d"),
        computeRevenueByProduct(orgId, "30d"),
        getMonthlyRevenue(orgId),
      ])
      setRevenue(rev)
      setCustomers(cust)
      setCustomerValue(custVal)
      setByProduct(prodRev)
      setMonthly(monthlyRev)
      setLoading(false)
    }
    load()
  }, [orgId])

  if (loading || !revenue) {
    return <div className="p-12 text-center text-muted-foreground text-sm">Loading commerce intelligence...</div>
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground max-w-xl">
        Where learning turns into revenue — which courses convert, who's buying, and what they're
        worth over time.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Gross Revenue"
          value={formatCurrency(revenue.grossRevenue.total)}
          change={revenue.grossRevenue.change}
          icon="DollarSign"
        />
        <KpiCard
          label="Net Revenue"
          value={formatCurrency(revenue.netRevenue.total)}
          change={revenue.netRevenue.change}
          icon="TrendingUp"
        />
        <KpiCard
          label="Paid Customers"
          value={revenue.paidCustomers.total}
          change={revenue.paidCustomers.change}
          icon="Users"
        />
        <KpiCard
          label="Avg. Order Value"
          value={formatCurrency(revenue.averageOrderValue.total)}
          change={revenue.averageOrderValue.change}
          icon="ShoppingCart"
        />
      </div>

      {!revenue.hasAnyOrders ? (
        <Card className="p-12 text-center dark:bg-transparent">
          <Icon name="LineChart" size={28} className="mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No commerce activity yet.</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="dark:bg-transparent">
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm text-foreground mb-1">Revenue by Product</h3>
                <p className="text-xs text-muted-foreground mb-2">Last 30 days</p>
                <div className="space-y-2">
                  {byProduct.map((p) => (
                    <div key={p.label} className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: p.color }} />
                      <span className="text-sm text-foreground truncate flex-1">{p.label}</span>
                      <span className="text-sm text-muted-foreground">{formatCurrency(p.value)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-transparent">
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm text-foreground mb-1">Revenue Trend</h3>
                <p className="text-xs text-muted-foreground mb-2">Trailing 12 months</p>
                <MiniBarChart data={monthly} valueKey="revenue" labelKey="label" />
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Total Customers" value={customers.totalCustomers.total} change={customers.totalCustomers.change} icon="Users" />
            <KpiCard label="New Customers" value={customers.newCustomers.total} change={customers.newCustomers.change} icon="UserPlus" />
            <KpiCard
              label="Returning Customers"
              value={customers.returningCustomers.total}
              change={customers.returningCustomers.change}
              icon="Repeat"
            />
            <KpiCard label="Organizations" value={customers.organizations.total} change={customers.organizations.change} icon="Building2" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard label="Avg. Spend (30d)" value={formatCurrency(customerValue.averageSpend)} icon="Wallet" />
            <KpiCard label="Lifetime Value" value={formatCurrency(customerValue.lifetimeValue)} icon="Gem" />
            <KpiCard label="Repeat Purchase Rate" value={`${customerValue.repeatPurchaseRate}%`} icon="RotateCw" />
            <KpiCard label="Retention Rate" value={`${customerValue.retentionRate}%`} icon="ShieldCheck" />
          </div>
        </>
      )}
    </div>
  )
}

export default IntelligenceCommercePanel